/**
 * UpgradePromptModal — global single-instance modal shown when any gated
 * action is blocked. Driven by the `useUpgradePromptStore`.
 *
 * Mounted once at the app root (see App.tsx). Content is looked up from the
 * shared GATES config using the gate key in the store; the modal has no
 * per-feature copy of its own.
 *
 * Upgrade CTA routes to `/settings?tab=billing` — the BillingTab reads
 * ?tab=billing and auto-selects itself.
 */

import {
  Button,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  VStack,
} from '@chakra-ui/react';
import { useEffect } from 'react';
import { useNavigate } from 'react-router';
import { GATES } from '@bower/shared/billing/gates';
import type { SubscriptionTier } from '@bower/shared';
import { useUpgradePromptStore } from '../../stores/upgradePromptStore';
import { subscribeUpgradeGateBlocked } from '../../services/events/upgradeGateEvents';
import { useWorkspacePlan } from '../../hooks/data/useBilling';
import { safeAnalytics } from '../../utils/analytics';
import { ANALYTICS_EVENTS } from '../../constants/analytics';
import { ROUTES } from '../../constants/routes';

export function UpgradePromptModal() {
  const { gateKey, close, open } = useUpgradePromptStore();
  const { data: plan } = useWorkspacePlan();
  const navigate = useNavigate();

  // Bridge the event bus (used by api.service's 402 interceptor and any
  // other non-UI emitter) into the Zustand store. This component is the
  // single subscriber — keeping the wiring inside the modal means the
  // store doesn't need its own window-listener lifecycle.
  useEffect(() => {
    return subscribeUpgradeGateBlocked(({ gateKey: eventGateKey, context }) => {
      open(eventGateKey, context);
    });
  }, [open]);

  if (!gateKey) return null;

  const gate = GATES[gateKey];
  const tier = (plan?.tier ?? 'FREE') as SubscriptionTier;

  const handleUpgrade = () => {
    safeAnalytics.track(ANALYTICS_EVENTS.BILLING_UPGRADE_PROMPTED, {
      gate: gateKey,
      required_tier: gate.requiredTier,
      current_tier: tier,
    } as never);
    close();
    // ?from=gate&gate=<key> lets BillingTab attribute the pageview to
    // the gate that drove it — feeds the "what converts best" insight
    // in the Billing dashboard.
    const params = new URLSearchParams({ tab: 'billing', from: 'gate', gate: gateKey });
    navigate(`${ROUTES.settings}?${params.toString()}`);
  };

  return (
    <Modal isOpen onClose={close} isCentered size="md">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{gate.title}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack align="start" spacing={3}>
            <Text>{gate.body(tier)}</Text>
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button onClick={handleUpgrade}>{gate.cta}</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
